/**
 * @fileoverview QuotaModal component for displaying quota exceeded information
 * Shows when users hit their usage limits with options to upgrade or dismiss
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuota } from '../../state/quotaStore.js';
import { formatResetTime, getQuotaMessage } from '../../lib/quota.js';

/**
 * QuotaModal Component
 * Displays quota exceeded information in a modal dialog
 */
function QuotaModal() {
  const navigate = useNavigate();
  const { lastQuota, isModalOpen, closeModal, clearQuota } = useQuota();

  /**
   * Handle navigation to billing/subscription page
   */
  const handleManagePlan = () => {
    closeModal();
    navigate('/billing'); // Adjust route as needed
  };

  /**
   * Handle modal dismissal
   */
  const handleDismiss = () => {
    closeModal();
  };

  /**
   * Handle complete quota clearing (for reset scenarios)
   */
  const handleClear = () => {
    clearQuota();
  };

  if (!isModalOpen || !lastQuota) {
    return null;
  }

  const resetTime = formatResetTime(lastQuota.resetAt);
  const quotaMessage = getQuotaMessage(lastQuota);

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="span">
            You've reached your plan limit
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
        >
          {quotaMessage}
        </Alert>

        {/* Usage Details */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Usage Details
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
            {lastQuota.scope && (
              <Chip 
                label={`${lastQuota.scope} Feature`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {lastQuota.planName && (
              <Chip 
                label={lastQuota.planName}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Usage Statistics */}
          <Box sx={{ 
            bgcolor: 'grey.50', 
            p: 2, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Used
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {lastQuota.used} / {lastQuota.limit}
              </Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Remaining
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold"
                color={lastQuota.remaining === 0 ? 'error.main' : 'text.primary'}
              >
                {lastQuota.remaining}
              </Typography>
            </Box>

            {/* Daily/Monthly limits if available */}
            {(lastQuota.dailyLimit || lastQuota.monthlyLimit) && (
              <>
                <Divider sx={{ my: 1 }} />
                {lastQuota.dailyLimit && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Daily Usage
                    </Typography>
                    <Typography variant="caption">
                      {lastQuota.dailyUsage || 0} / {lastQuota.dailyLimit}
                    </Typography>
                  </Box>
                )}
                {lastQuota.monthlyLimit && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Monthly Usage
                    </Typography>
                    <Typography variant="caption">
                      {lastQuota.monthlyUsage || 0} / {lastQuota.monthlyLimit}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Reset Information */}
        {resetTime && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              p: 1.5,
              bgcolor: 'info.light',
              color: 'info.contrastText',
              borderRadius: 1,
              mb: 2
            }}
          >
            <ScheduleIcon fontSize="small" />
            <Typography variant="body2">
              <strong>Resets:</strong> {resetTime}
            </Typography>
          </Box>
        )}

        {/* Additional Information */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Upgrade your plan for higher limits and unlimited access to premium features.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleDismiss}
          variant="outlined"
          color="inherit"
        >
          OK
        </Button>
        <Button
          onClick={handleManagePlan}
          variant="contained"
          color="primary"
          startIcon={<TrendingUpIcon />}
          sx={{ ml: 1 }}
        >
          Manage Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuotaModal;
